define('presentation/settings/outboundCallingCampaigns/viewModels/addOutboundCallingCampaignViewModel',[
    'businessServices/authentication/accountPlanInfo',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'common/url/urlFormatter',
    'constants/colorConstants',
    'constants/liveReceptionistScriptConstants',
    'constants/liveReceptionistScriptTypeConstants',
    'presentation/common/window/windowControl',
    'presentation/settings/outboundCallingCampaigns/facades/addOutboundCallingCampaignFacade',
    'presentation/settings/outboundCallingCampaigns/validators/addOutboundCallingCampaignViewModelValidator',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const SorterConstructor = require('common/collections/collectionSorter');
        const _sorter = new SorterConstructor();

        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');

        const _accountPlanInfo = require('businessServices/authentication/accountPlanInfo');
        const _i18n = require('i18next');
        const _commonState = require('common/storage/commonState');
        const _liveReceptionistScriptConstants = require('constants/liveReceptionistScriptConstants');
        const _liveReceptionistScriptTypeConstants = require('constants/liveReceptionistScriptTypeConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _windowControl = require('presentation/common/window/windowControl');
        const _urlFormatter = require('common/url/urlFormatter');

        let _disposables = [];
        let _facade = null;
        let _validator = null;

        const NONE_LIVE_RECEPTIONIST_SCRIPT = {
            id: 0,
            name: _i18n.t('addOutboundCallingCampaign:liveReceptionistScriptNone'),
            type: _commonState.types.liveReceptionistScript
        };

        self.modelStateObserver = null;
        self.shouldScrollToTop = ko.observable(false);
        self.isCompositionComplete = ko.observable(false);
        self.outboundCallingCampaignId = null;
        self.isNewOutboundCallingCampaign = ko.observable(false);

        self.isImpersonating = ko.pureComputed(() => _sessionAccountInfo.isImpersonating());
        self.headerI18nKey = ko.pureComputed(() => self.isNewOutboundCallingCampaign() ?
            "addOutboundCallingCampaign:addHeader" : "addOutboundCallingCampaign:editHeader");
        self.saveButtonTitle = ko.pureComputed(() => self.isNewOutboundCallingCampaign() ?
            _i18n.t('addOutboundCallingCampaign:add') : _i18n.t('addOutboundCallingCampaign:save'));

        self.name = ko.observable('').extend({observeState: true});
        self.countryAbbreviation = ko.observable('us').extend({observeState: true});
        self.outboundCallerId = ko.observable('').extend({observeState: true});
        self.campaignCode = ko.observable('').extend({observeState: true});
        self.genesysCampaignId = ko.observable('').extend({observeState: true});
        self.active = ko.observable('').extend({observeState: true});

        self.selectedLiveReceptionistScript = ko.observable(null).extend({observeState: true});
        self.addLiveReceptionistScriptUrl = _navigationConfiguration.routesById.addLiveReceptionistScript.url;
        self.outboundCallingCampaignsUrl = _navigationConfiguration.routesById.outboundCallingCampaigns.url;
        self.availableLiveReceptionistScripts = ko.observableArray([]);
        self.placeholderMessage = _i18n.t('addOutboundCallingCampaign:pleaseSelect');
        self.liveReceptionistScriptsIsEnabled = ko.pureComputed(() => {
            return !self.showNoOutboundCallingPackageWarningMessage() && !self.showNoLiveReceptionistScriptsWarningMessage();
        });
        self.liveReceptionistScripts = ko.pureComputed(() => {
            const liveReceptionistScripts = self.availableLiveReceptionistScripts()
                .filter(script => isActiveOutboundCallingScript(script) || isSelectedScript(script));
            _sorter.sort(liveReceptionistScripts, "name", true);
            return liveReceptionistScripts;
        });

        self.createLiveReceptionistScriptLinkText = _i18n.t('addOutboundCallingCampaign:createLiveReceptionistScript');
        self.noLiveReceptionistScriptsWarningMessage = _i18n.t('addOutboundCallingCampaign:noLiveReceptionistScriptWarningMessage');
        self.noOutboundCallingPackageWarningMessage = _i18n.t('addOutboundCallingCampaign:noOutboundCallingPackageWarningMessage');
        self.showNoLiveReceptionistScriptsWarningMessage = ko.pureComputed(() => self.liveReceptionistScripts().length === 0);
        self.showNoOutboundCallingPackageWarningMessage = ko.pureComputed(() => {
            return self.isImpersonating() && (_accountPlanInfo.hasOutboundCallingPackage() === false || _accountPlanInfo.isOutboundCallingOnboardingComplete() === false);
        });
        self.showValidationWarnings = ko.observable(false);
        self.showGenesysCampaignIdLink = ko.observable(self.isImpersonating());
        self.genesysCampaignUrl = ko.pureComputed( () => self.genesysCampaignId().length > 0 ?
            _urlFormatter.buildGenesysOutboundCampaignUrl(self.genesysCampaignId()) : '');

        self._formatUuid = (uuid) => {
            return (uuid.substring(0, 8) + "-" + uuid.substring(8, 12) + "-" + uuid.substring(12, 16) + "-" +
                uuid.substring(16, 20) + "-" + uuid.substring(20, 32));
        };

        self.save = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                if (self.selectedLiveReceptionistScript() === NONE_LIVE_RECEPTIONIST_SCRIPT) {
                    self.selectedLiveReceptionistScript(null);
                }

                self.validate()
                    .fail(promise.reject)
                    .done((isValid) => {
                        if (isValid === false) {
                            promise.resolve();
                            return;
                        }

                        const outboundCallingCampaign = {
                            outboundCallingCampaignId: self.outboundCallingCampaignId || null,
                            name: self.name(),
                            campaignCode: self.campaignCode(),
                            outboundCallerId: self.outboundCallerId(),
                            liveReceptionistScriptId: self.selectedLiveReceptionistScript() ? self.selectedLiveReceptionistScript().id : null
                        };

                        const facadePromise = self.isNewOutboundCallingCampaign() ?
                            _facade.createOutboundCallingCampaign(outboundCallingCampaign) :
                            _facade.updateOutboundCallingCampaign(outboundCallingCampaign);

                        facadePromise
                            .fail(promise.reject)
                            .done(() => {
                                self.modelStateObserver.saveData();
                                _router.navigate(self.outboundCallingCampaignsUrl);
                                promise.resolve();
                            });
                    });
            });
        };

        self.cancelForm = () => {
            return _promiseFactory.defer((promise) => {
                self.modelStateObserver.navigateWithoutWarning = true;
                _router.navigate(self.outboundCallingCampaignsUrl);
                promise.resolve();
            });
        };

        self.validate = () => {
            self.shouldScrollToTop(false);
            return _promiseFactory.defer((promise) => {
                _validator.validate()
                    .fail(promise.reject)
                    .done((isFormValid) => {
                        if (isFormValid === false) {
                            self.showValidationWarnings(true);
                        }
                        promise.resolve(isFormValid);
                    });
            });
        };

        self.openGenesysCampaign = () => {
            _windowControl.openNewWindow(self.genesysCampaignUrl());
        };

        const isActiveOutboundCallingScript = (script) => {
            return script.status === _liveReceptionistScriptConstants.status.active.id &&
                script.scriptType === _liveReceptionistScriptTypeConstants.OutboundCalling;
        };

        const isSelectedScript = (script) => {
            return self.selectedLiveReceptionistScript() !== null &&
                self.selectedLiveReceptionistScript() !== undefined &&
                self.selectedLiveReceptionistScript().id === script.id;
        };

        const _setLiveReceptionistScript = (liveReceptionistScriptId) => {
            if (liveReceptionistScriptId === null) {
                self.selectedLiveReceptionistScript(NONE_LIVE_RECEPTIONIST_SCRIPT);
                return;
            }

            const liveReceptionistScript = self.liveReceptionistScripts().find(script => script.id === liveReceptionistScriptId);
            self.selectedLiveReceptionistScript(liveReceptionistScript || NONE_LIVE_RECEPTIONIST_SCRIPT);
        };

        const _populateExistingOutboundCallingCampaignForm = (outboundCallingCampaignId) => {
            return _promiseFactory.deferIndefinitely((promise) => {
                _facade.getOutboundCallingCampaign(outboundCallingCampaignId)
                    .fail(promise.reject)
                    .done((campaign) => {
                        const {
                            name,
                            campaignCode,
                            active,
                            outboundCallerId,
                            liveReceptionistScriptId,
                            genesysCampaignId
                        } = campaign;

                        self.name(name);
                        self.campaignCode(campaignCode);
                        self.active(active);
                        self.outboundCallerId(outboundCallerId);
                        self.genesysCampaignId(self._formatUuid(genesysCampaignId));

                        _setLiveReceptionistScript(liveReceptionistScriptId);

                        promise.resolve();
                    });
            });
        };

        const _generateCampaignCode = () => {
            const length = 8;
            const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
            let result = "";
            const charsLength = chars.length;

            for (let i = 0; i < length; i++) {
                const randomIndex = Math.floor(Math.random() * charsLength);
                result += chars[randomIndex];
            }

            return result;
        };

        const _populateNewOutboundCallingCampaignForm = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                self.campaignCode(_generateCampaignCode());
                _setLiveReceptionistScript(null);
                promise.resolve();
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
            if (self.outboundCallingCampaignId === null) {
                self.isNewOutboundCallingCampaign(true);
            }
        };

        self.detached = () => {
            self.isCompositionComplete(false);
            _disposables.forEach(d => d.dispose());
            _disposables = [];
        };

        self.activate = (outboundCallingCampaignId) => {
            if (outboundCallingCampaignId) {
                self.outboundCallingCampaignId = outboundCallingCampaignId;
            }

            const FacadeConstructor = require('presentation/settings/outboundCallingCampaigns/facades/addOutboundCallingCampaignFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/settings/outboundCallingCampaigns/validators/addOutboundCallingCampaignViewModelValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            self.modelStateObserver = new ModelStateObserverConstructor(self, true);

            let initPromiseFactory = new PromiseFactoryConstructor();

            initPromiseFactory.defer((initPromise) => {
                _facade.getLiveReceptionistScripts()
                    .fail(initPromise.reject)
                    .done((liveReceptionistScripts) => {
                        self.availableLiveReceptionistScripts(liveReceptionistScripts);
                        let populatePromise;
                        if (self.outboundCallingCampaignId) {
                            populatePromise = _populateExistingOutboundCallingCampaignForm(self.outboundCallingCampaignId);
                        } else {
                            populatePromise = _populateNewOutboundCallingCampaignForm();
                        }
                        populatePromise.done(() => {
                            initPromise.resolve();
                        });
                    });
            });

            _promiseFactory.defer((initializePromise) => {
                initPromiseFactory.wait()
                    .done(() => {
                        self.modelStateObserver.commitData();
                        _validator.registerViewModel(self);
                        initializePromise.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});
